import { useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, RefreshCw } from 'lucide-react'
import type { AccountsAdminApi } from '../adminApi.js'
import type { SignInUser, SignInUserKind } from '../index.js'
import { Modal, Skeleton } from '../../core/index.js'
import { formatDateTime } from '../../core/index.js'
import { SortableTh, useSort, type SortAccessors } from '../../core/index.js'
import { AdminIcon, FacilityAdministratorIcon, OperatorIcon, RootAdminIcon, SalesIcon, WorkerIcon } from '../../core/index.js'
import { Button } from '../../core/index.js'
import { LoadFailure } from '../../core/index.js'

/** The admin calls this screen uses. */
type AdminApi = AccountsAdminApi

const KIND: Record<SignInUserKind, { label: string; Icon: typeof OperatorIcon }> = {
  root: { label: 'Root admin', Icon: RootAdminIcon },
  admin: { label: 'Admin', Icon: AdminIcon },
  sales: { label: 'Sales', Icon: SalesIcon },
  operator: { label: 'Operator', Icon: OperatorIcon },
  dashboard: { label: 'Operator (shared login)', Icon: OperatorIcon },
  facility_administrator: { label: 'Facility administrator', Icon: FacilityAdministratorIcon },
  worker: { label: 'Worker', Icon: WorkerIcon },
}

const SORT: SortAccessors<SignInUser, 'person' | 'role' | 'organization' | 'last' | 'count'> = {
  person: (user) => (user.name ?? user.login).toLowerCase(),
  role: (user) => KIND[user.userKind].label,
  organization: (user) => user.organizationName ?? '',
  last: (user) => user.lastAt,
  count: (user) => user.count,
}

/** "Chrome on macOS" from a user agent, as far as it can be told at a glance; the full string is the row's title. */
function browserOf(userAgent: string | null): string {
  if (!userAgent) {
    return 'Not recorded'
  }
  const browser = /Edg\//.test(userAgent) ? 'Edge' : /Chrome\//.test(userAgent) ? 'Chrome' : /Firefox\//.test(userAgent) ? 'Firefox' : /Safari\//.test(userAgent) ? 'Safari' : /Postman/i.test(userAgent) ? 'Postman' : null
  const system = /iPhone|iPad/.test(userAgent) ? 'iOS' : /Android/.test(userAgent) ? 'Android' : /Mac OS X/.test(userAgent) ? 'macOS' : /Windows/.test(userAgent) ? 'Windows' : /Linux/.test(userAgent) ? 'Linux' : null
  return browser && system ? `${browser} on ${system}` : (browser ?? system ?? 'Other')
}

function SignInHistoryModal({ api, user, onClose }: { api: AdminApi; user: SignInUser; onClose: () => void }) {
  const eventsQuery = useQuery({ queryKey: ['admin', 'sign-ins', 'events', user.userKey], queryFn: () => api.listSignInEvents(user.userKey) })
  return (
    <Modal
      eyebrow={KIND[user.userKind].label}
      title={user.name ?? user.login}
      subtitle={
        <>
          {user.login}
          {user.organizationName && <> · {user.organizationName}</>} · {user.count} {user.count === 1 ? 'sign-in' : 'sign-ins'} on record
        </>
      }
      onClose={onClose}
      footer={
        <Button icon={Check} onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="px-6 py-4">
        {eventsQuery.isPending ? (
          <Skeleton label="Loading sign-ins" />
        ) : eventsQuery.isError ? (
          <LoadFailure size="xs" message="Could not load this person's sign-ins." onRetry={() => void eventsQuery.refetch()} retrying={eventsQuery.isFetching} />
        ) : (
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-marine/15 text-[10px] uppercase tracking-wide text-ink-mid">
                <th className="py-2 pr-4 font-bold">#</th>
                <th className="py-2 pr-4 font-bold">Signed in</th>
                <th className="py-2 font-bold">Browser</th>
              </tr>
            </thead>
            <tbody>
              {eventsQuery.data.map((event, index) => (
                <tr key={event.eventId} className="border-b border-marine/10">
                  <td className="py-2 pr-4 text-ink-mid font-mono">{eventsQuery.data.length - index}</td>
                  <td className="py-2 pr-4 text-ink whitespace-nowrap">{formatDateTime(event.at)}</td>
                  <td className="py-2 text-ink-mid" title={event.userAgent ?? undefined}>
                    {browserOf(event.userAgent)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Modal>
  )
}

/**
 * The root admin's page, and nobody else's: everyone who has signed in -
 * SeeWhoCares's admins and every client's operators, facility
 * administrators and workers - with when they last did and how often.
 * Opening a person shows each of their sign-ins. Only accepted passwords
 * are here: a refused attempt and a demo session opened as someone are not
 * that person signing in. The record starts when this was deployed.
 */
export function SignInsPage({ api }: { api: AdminApi }) {
  const queryClient = useQueryClient()
  const usersQuery = useQuery({ queryKey: ['admin', 'sign-ins'], queryFn: api.listSignInUsers })
  // The list and any person's history already opened: both sit under this key, so one press brings everything up to date.
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['admin', 'sign-ins'] })
  const [query, setQuery] = useState('')
  const [viewing, setViewing] = useState<SignInUser | null>(null)
  const needle = query.trim().toLowerCase()
  const filtered = useMemo(
    () => (usersQuery.data ?? []).filter((user) => needle === '' || `${user.name ?? ''} ${user.login} ${user.organizationName ?? ''} ${KIND[user.userKind].label}`.toLowerCase().includes(needle)),
    [usersQuery.data, needle],
  )
  const { sorted, header } = useSort(filtered, SORT)

  return (
    <div className="max-w-5xl mx-auto px-7 py-6">
      {viewing && <SignInHistoryModal api={api} user={viewing} onClose={() => setViewing(null)} />}
      <p className="max-w-2xl text-xs leading-relaxed text-ink-mid mb-4">
        Only the root admin sees this page. It lists everyone with a sign-in on record since the history began, latest first; open a person for each of their
        sign-ins. A wrong password is not recorded, and neither is a demo session opened as someone.
      </p>
      <div className="flex items-center justify-between gap-4 mb-4">
        <div className="text-sm text-ink-mid">{usersQuery.isSuccess && `${filtered.length} ${filtered.length === 1 ? 'person' : 'people'}`}</div>
        <div className="flex items-center gap-2">
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Find a person, login or organization"
            aria-label="Find a person, login or organization"
            className="w-72 border border-marine/30 bg-white px-2.5 py-1.5 text-xs text-ink"
          />
          <button
            type="button"
            onClick={refresh}
            disabled={usersQuery.isFetching}
            title={usersQuery.isFetching ? 'Refreshing…' : 'Refresh'}
            aria-label={usersQuery.isFetching ? 'Refreshing' : 'Refresh'}
            className="inline-flex items-center rounded border border-marine/30 bg-white p-2 text-marine hover:bg-marine-lt/40 disabled:opacity-60"
          >
            <RefreshCw aria-hidden="true" strokeWidth={2.25} className={`h-3.5 w-3.5 ${usersQuery.isFetching ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>
      {usersQuery.isPending ? (
        <Skeleton lines={5} label="Loading sign-ins" />
      ) : usersQuery.isError ? (
        <LoadFailure message="Could not load the sign-in history. If the API was deployed before it existed, deploy it to switch this on." onRetry={() => void usersQuery.refetch()} retrying={usersQuery.isFetching} />
      ) : sorted.length === 0 ? (
        <div className="bg-white border border-marine/15 p-6 text-sm text-ink-mid">{needle ? 'Nobody matches.' : 'No sign-ins on record yet. They appear here from the next time anyone signs in.'}</div>
      ) : (
        <div className="bg-white border border-marine/15 overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-marine/15 text-[10px] uppercase tracking-wide text-ink-mid">
                <SortableTh className="px-4 py-2" label="Person" {...header('person')} />
                <SortableTh className="px-4 py-2" label="Role" {...header('role')} />
                <SortableTh className="px-4 py-2" label="Organization" {...header('organization')} />
                <SortableTh className="px-4 py-2" label="Last sign-in" {...header('last')} />
                <SortableTh className="px-4 py-2" label="Sign-ins" {...header('count')} />
              </tr>
            </thead>
            <tbody>
              {sorted.map((user) => {
                const { Icon, label } = KIND[user.userKind]
                return (
                  <tr key={user.userKey} className="border-b border-marine/10">
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-2">
                        <Icon className="w-4 h-4 text-marine" />
                        <span>
                          <button type="button" onClick={() => setViewing(user)} className="font-bold text-ink text-left underline decoration-marine/30 underline-offset-2 hover:text-marine hover:decoration-marine">
                            {user.name ?? user.login}
                          </button>
                          {user.name && <span className="block text-[11px] text-ink-mid">{user.login}</span>}
                        </span>
                      </span>
                    </td>
                    <td className="px-4 py-3 text-ink-mid">{label}</td>
                    <td className="px-4 py-3 text-ink">{user.organizationName ?? (user.clientId === null ? 'SeeWhoCares' : '—')}</td>
                    <td className="px-4 py-3 text-ink-mid whitespace-nowrap">{formatDateTime(user.lastAt)}</td>
                    <td className="px-4 py-3 text-ink-mid">{user.count}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
